import * as React from "react";
import { motion } from "framer-motion";

import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import InlineAlert from "@/components/providers/inline-alert-msg";
import { getErrorMessage } from "@/hooks/redux-hooks";
import { Link } from "react-router";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
};

interface ResetPasswordFormProps extends React.HTMLAttributes<HTMLDivElement> {
  handleResetPassword: (data: SSOResendVerificationPayload) => void;
  isLoading: boolean;
  isError: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
}

const FormSchema = z.object({
  username: z.string().email("Email is required to reset your password"),
});

export default function ResetPasswordForm({
  className,
  handleResetPassword,
  isLoading,
  isError,
  error,
}: ResetPasswordFormProps) {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const res: SSOResendVerificationPayload = {
      username: data.username,
      verification_type: "reset_password",
    };
    handleResetPassword(res);
  };

  return (
    <motion.div
      className={cn("grid gap-6", className)}
      initial="hidden"
      animate="visible"
      variants={formVariants}
    >
      {isError && error && (
        <InlineAlert
          title="Reset password Failed"
          message={getErrorMessage(error)}
          mtype={"error"}
        />
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <motion.div className="grid gap-2" variants={itemVariants}>
            <motion.div className="grid gap-1" variants={itemVariants}>
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>

            <div className="flex gap-1 mb-2 mt-2 justify-between">
              <span className="text-muted-foreground text-sm">
                <Link to="/auth/login">I remember my password</Link>
              </span>
            </div>

            <Button disabled={isLoading}>
              {isLoading && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reset password
            </Button>
          </motion.div>
        </form>
      </Form>
    </motion.div>
  );
}
